import cls from 'classnames';
import React, { useEffect, useState, useCallback, useRef, forwardRef } from 'react';

import type { AutoResizeTextareaProps } from './autoresize';
import { AutoResizeTextarea } from './autoresize';
import styles from './index.module.scss';

export const ITextArea = forwardRef<
  HTMLTextAreaElement,
  AutoResizeTextareaProps & {
    wrapperCls?: string;
    wrapperStyle?: React.CSSProperties;
    maxLength?: number;
    showCount?: boolean;
    status?: 'error' | 'default';
    disabled?: boolean;
  }
>((props, ref) => {
  const {
    maxLength,
    showCount = false,
    wrapperCls,
    wrapperStyle,
    value = '',
    status = 'default',
  } = props;

  const [val, setVal] = useState(value);
  const isComposing = useRef(false);

  useEffect(() => {
    setVal(value);
  }, [value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setVal(e.target.value);
      if (!isComposing.current && props.onChange) props.onChange(e);
    },
    [isComposing, props]
  );

  const handleCompositionStart = useCallback(() => {
    isComposing.current = true;
  }, []);

  const handleCompositionEnd = useCallback((e: React.CompositionEvent<HTMLTextAreaElement>) => {
    isComposing.current = false;
    handleInputChange(e as unknown as React.ChangeEvent<HTMLTextAreaElement>);
  }, []);

  return (
    <div
      className={cls(styles.textArea, wrapperCls, {
        [styles.error]: status === 'error',
      })}
      style={wrapperStyle}
    >
      <AutoResizeTextarea
        {...props}
        ref={ref}
        value={val}
        onChange={handleInputChange}
        maxLength={maxLength}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
      />
      {showCount && maxLength && (
        <section>
          {val.length}/{maxLength}
        </section>
      )}
    </div>
  );
});

ITextArea.displayName = 'ITextArea';
